import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import logo from '../Images/MyLogo.png';
import { Moon, Sun, Menu, X } from 'lucide-react';

const Nav = ({ activeSection, setActiveSection, navScrolled }) => {
  const [isDarkMode, setIsDarkMode] = useState(true);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  // Warm light palette tokens — keep in sync with CSS [data-theme="light"]
  const L = {
    navBg:        'rgba(250, 247, 244, 0.96)',
    navBgClear:   'rgba(250, 247, 244, 0.12)',
    linkColor:    '#1c1410',
    linkActive:   '#c2521a',
    linkActiveBg: 'rgba(194, 82, 26, 0.10)',
    linkHoverBg:  'rgba(194, 82, 26, 0.06)',
    indicator:    'linear-gradient(90deg, #c2521a, #e07b39)',
    toggleBg:     'rgba(194, 82, 26, 0.10)',
    toggleColor:  '#c2521a',
  };
  const D = {
    navBg:        'rgba(15, 15, 35, 0.95)',
    navBgClear:   'rgba(15, 15, 35, 0.10)',
    linkColor:    '#fff',
    linkActive:   '#667eea',
    linkActiveBg: 'rgba(102, 126, 234, 0.2)',
    linkHoverBg:  'rgba(102, 126, 234, 0.1)',
    indicator:    'linear-gradient(90deg, #667eea, #764ba2)',
    toggleBg:     'rgba(255, 255, 255, 0.10)',
    toggleColor:  '#fff',
  };
  const T = isDarkMode ? D : L;

  const toggleTheme = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    document.body.style.transition = 'background-color 0.6s ease, color 0.6s ease';
    const next = !isDarkMode;
    setIsDarkMode(next);
    document.body.setAttribute('data-theme', next ? 'dark' : 'light');
    localStorage.setItem('theme', next ? 'dark' : 'light');
    setTimeout(() => setIsAnimating(false), 700);
  };

  useEffect(() => {
    document.body.setAttribute('data-theme', 'dark');
    document.body.style.transition = 'none';
    setTimeout(() => { document.body.style.transition = 'background-color 0.6s ease, color 0.6s ease'; }, 80);
  }, []);

  return (
    <motion.nav
      className="nav"
      style={{
        background: navScrolled ? T.navBg : T.navBgClear,
        backdropFilter: navScrolled ? 'blur(14px)' : 'none',
        WebkitBackdropFilter: navScrolled ? 'blur(14px)' : 'none',
        borderBottom: navScrolled
          ? isDarkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(194,82,26,0.12)'
          : '1px solid transparent',
        transition: 'background 0.5s ease, border-color 0.5s ease',
      }}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="nav-container">
        <motion.div className="logo" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
          <motion.img
            src={logo}
            alt="Logo"
            style={{ width: '50px', height: '50px', borderRadius: '50%', objectFit: 'contain' }}
            animate={{ scale: [1, 1.12, 1] }}
            transition={{ duration: 3.5, repeat: Infinity }}
          />
        </motion.div>

        <button className="nav-toggle" onClick={() => setIsMenuOpen(!isMenuOpen)}
          style={{ color: T.linkColor }}>
          {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>

        <div className={`nav-links ${isMenuOpen ? 'active' : ''}`}>
          {['home', 'about', 'projects', 'contact'].map((section) => (
            <motion.a
              key={section}
              href={`#${section}`}
              className="nav-link"
              style={{
                color: activeSection === section ? T.linkActive : T.linkColor,
                background: activeSection === section ? T.linkActiveBg : 'transparent',
                transition: 'color 0.4s ease, background 0.4s ease',
              }}
              onClick={(e) => {
                e.preventDefault();
                setActiveSection(section);
                setIsMenuOpen(false);
                document.getElementById(section)?.scrollIntoView({ behavior: 'smooth' });
              }}
              whileHover={{ y: -2, background: T.linkHoverBg }}
              whileTap={{ scale: 0.95 }}
            >
              {section.charAt(0).toUpperCase() + section.slice(1)}
              {activeSection === section && (
                <motion.div
                  className="nav-indicator"
                  layoutId="navIndicator"
                  style={{ background: T.indicator, transition: 'background 0.4s ease' }}
                />
              )}
            </motion.a>
          ))}

          <motion.button
            className="theme-toggle"
            onClick={toggleTheme}
            whileHover={{ scale: 1.12 }}
            whileTap={{ scale: 0.88 }}
            style={{ background: T.toggleBg, color: T.toggleColor, transition: 'all 0.4s ease' }}
            aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            <div className="theme-icon-container">
              <motion.div
                key={isDarkMode ? 'moon' : 'sun'}
                initial={{ rotate: -90, opacity: 0, scale: 0.5 }}
                animate={{ rotate: 0,   opacity: 1, scale: 1   }}
                exit={{    rotate:  90, opacity: 0, scale: 0.5 }}
                transition={{ duration: 0.35 }}
              >
                {isDarkMode ? <Moon size={20} /> : <Sun size={20} />}
              </motion.div>
            </div>
          </motion.button>
        </div>
      </div>
    </motion.nav>
  );
};

export default Nav;
